import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Dropdown } from 'semantic-ui-react'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import DrawerEditDairy from './DrawerEditDairy'
import { CO2_DEFAULT_PREFIX, CO2_KLIMA_PREFIX } from '~/api/services/farmers'

interface Props {
  co2ModuleEnabled: boolean
  climaCheckModuleEnabled:boolean
}

const DairyData: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { climaCheckModuleEnabled, co2ModuleEnabled } = props

  const [year, setYear] = useState('')
  const [editDairyData, setEditDairyData] = useState(null)

  const co2Prefix = climaCheckModuleEnabled ? CO2_KLIMA_PREFIX : CO2_DEFAULT_PREFIX

  const tableColumnHeaders = [
    {
      name: t('Month'),
      type: 'date',
      format: 'MMM YYYY',
      key: 'month'
    },
    {
      name: t('Milk sold'),
      key: 'amount',
      type: 'number-whole',
      amountUnit: 'kg'
    },
    {
      name: t('Net milk price including surcharges'),
      type: 'value-with-unit',
      key: 'netPrice',
      amountUnit: '€ Ct/kg'
    },
    {
      name: t('Protein content of milk sold'),
      key: 'protein',
      type: 'value-with-unit',
      decimalScale: 2,
      amountUnit: '%'
    },
    {
      name: t('Fat content of milk sold'),
      key: 'fat',
      type: 'value-with-unit',
      decimalScale: 2,
      amountUnit: '%'
    },
    {
      name: t('Urea content'),
      key: 'ureaContent',
      type: 'value-with-unit',
      decimalScale: 2,
      amountUnit: 'ppm'
    },
    {
      name: t('Actions')
    }
  ]

  const actions = [
    {
      popupMessage: t('Edit'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit'
    }
  ]

  const {
    data: years,
    isLoading: isLoadingOptions
  } = FarmersQuery.getDairyOptions(id, co2Prefix)

  const {
    data: dairyData,
    isLoading: isLoadingData,
    isFetching: isFetchingData,
    refetch: refetchDairyData
  } = FarmersQuery.getDairyData(id, { year }, co2Prefix)

  useEffect(() => {
    if (years?.length > 0) {
      setYear(years[0]?.key)
    }
  }, [years])

  useEffect(() => {
    refetchDairyData()
  }, [year])

  function renderFilters() {
    return <Dropdown
      selectOnBlur={false}
      selection
      value={year}
      options={years?.map(year => ({ value: year.key, text: year.label })) || []}
      onChange={(e, data) => setYear(data.value as string)}
    />
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'edit':
        return setEditDairyData(row)
      default:
        return
    }
  }

  function renderTable() {
    const filteredTableColumns = co2ModuleEnabled ? tableColumnHeaders.filter(tableColumn => tableColumn.key !== 'ureaContent') : tableColumnHeaders
    return <TableComponent
      tableColumnHeaders={filteredTableColumns}
      isFetchingData={isFetchingData}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      animated
      removePagination
      idKey={'month'}
      actions={actions}
      rows={dairyData || []}
      total={dairyData?.length || 0}
    />
  }

  function renderDairyDrawer() {
    return <DrawerEditDairy
      handleHiddenDrawer={(submited) => {
        setEditDairyData(null);
        if (submited) {
          refetchDairyData();
        }
      }}
      dairyData={editDairyData}
      co2Prefix={co2Prefix}
      climaCheckModuleEnabled={climaCheckModuleEnabled}
    />
  }

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: t('CO2 data') }}/>}
    childContainerSize={'300px'}
    fontSizeHeader={'18px'}
    isBusy={isLoadingOptions || isLoadingData}
    useSpinner
    filters={renderFilters()}
  >
    {renderTable()}
    {renderDairyDrawer()}
  </Layout>
}
export default DairyData